import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'urql';
import { useTranslation, Trans } from 'react-i18next';

import { DeleteProjectDocument, Project } from '../../gql/graphql';

type DeleteProjectTabProps = {
  project: Pick<Project, 'name' | 'id'>;
  closeModal: () => void;
};

type DeleteProjectFormData = {
  title: string;
};

export default function DeleteProjectTab({
  project,
  closeModal,
}: DeleteProjectTabProps): React.ReactElement {
  const { t } = useTranslation('company');
  const toast = useToast();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<DeleteProjectFormData>({
    mode: 'onChange',
  });

  const [deleteProjectResult, deleteProject] = useMutation(
    DeleteProjectDocument
  );

  const onSubmit = handleSubmit(async () => {
    const result = await deleteProject({ input: { id: project.id } });
    if (result.error) {
      toast({
        title: t('project-settings.delete.error.delete'),
        status: 'error',
      });
    } else {
      toast({
        title: t('project-settings.delete.success.delete'),
        status: 'success',
      });
      closeModal();
      navigate('/projects/');
    }
  });

  return (
    <VStack spacing={2} alignItems={'start'}>
      <form onSubmit={onSubmit} id="delete-project-form">
        <Alert status="warning" borderRadius={'lg'}>
          <AlertIcon />
          {t('project-settings.delete.warning')}
        </Alert>
        <FormControl mt={2}>
          <FormLabel>
            <Trans
              defaults={t('project-settings.delete.confirm-label')}
              values={{ projectName: project.name }}
              components={{ strong: <strong /> }}
            />
          </FormLabel>
          <Input
            autoFocus
            {...register('title', {
              validate: (value) => value === project.name,
            })}
            type="text"
            placeholder={t('project-settings.delete.project-name-placeholder')}
          ></Input>
        </FormControl>
      </form>
      <Button
        disabled={!isValid}
        alignSelf={'end'}
        isLoading={deleteProjectResult.fetching}
        type="submit"
        form="delete-project-form"
        colorScheme="red"
        variant={'outline'}
      >
        {t('project-settings.delete.delete-project')}
      </Button>
    </VStack>
  );
}
